import {useContext, useEffect, useRef, useState} from "react"
import {useParams, useNavigate, useSearchParams, useRouteLoaderData} from "react-router-dom"
import {Skeleton} from "primereact/skeleton"
import Alert from "react-bootstrap/Alert"
import {default as cx} from "classnames"
import useSWR from "swr"

import EditUser from "./EditUser"
import SubHeader from "../SubHeader.jsx"
import RoleTabs, {getVisibleTabs} from "./RolesTab"
import {fetcherGet} from "../../utilities/general"
import {wrappedGet} from "../../utilities/AxiosHelper"
import {getToken} from "../../utilities/useToken"
import RefContext from "../../utilities/RefContext"
import OrienteeDetails from "../OrienteeDetails"

const ORIENTEE_TABS = ["Edit User & Role", "Edit Orientation Details", "Orientation Progress Details"]
const TAB_SLUGS = {
    "Edit User & Role": "user",
    "Edit Orientation Details": "orientation",
    "Orientation Progress Details": "progress",
}
const SLUG_TABS = Object.fromEntries(Object.entries(TAB_SLUGS).map(([label, slug]) => [slug, label]))

const EMPTY_ROWS = []
const EMPTY_ORIENTATION = {}

function EditUserPage() {
    const {readOnly} = useRouteLoaderData("admin-users") ?? {readOnly: "true"}
    const {id} = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const {role} = getToken() || {}
    const add = !id

    const tab = SLUG_TABS[searchParams.get("tab")] ?? ORIENTEE_TABS[0]
    const changeTab = (nextTab) => {
        const next = new URLSearchParams(searchParams)
        next.set("tab", TAB_SLUGS[nextTab])
        setSearchParams(next)
    }
    const [orientation, setOrientation] = useState(EMPTY_ORIENTATION)
    const [exporting, setExporting] = useState(false)
    const hydratedRef = useRef(false)
    const {dispatch} = useContext(RefContext)

    const {data: user, isLoading} = useSWR(!add ? `/user/${id}` : null, fetcherGet, {fallbackData: EMPTY_ROWS})
    const isOrientee = user?.role_name === "Orientee"

    /* Orientation data is keyed on orientation_id, not user_id — resolve it first. */
    const {data: orientationRows} = useSWR(!add && isOrientee ? `/get-orientations?user-id=${id}` : null, fetcherGet, {
        fallbackData: EMPTY_ROWS,
    })

    const requestedOrientationId = searchParams.get("orientation")

    const orientationId =
        orientationRows.find((r) => String(r.orientation_id) === requestedOrientationId)?.orientation_id ??
        orientationRows[0]?.orientation_id

    const {data: orientationData, isLoading: isOrientationLoading} = useSWR(
        orientationId ? `/orientees/${orientationId}` : null,
        fetcherGet,
        {fallbackData: EMPTY_ORIENTATION}
    )

    /* Hydrate the form once. Later revalidations must not stomp in-progress edits. */
    useEffect(() => {
        if (hydratedRef.current || isOrientationLoading || !orientationData?.id) return
        setOrientation(orientationData)
        hydratedRef.current = true
    }, [orientationData, isOrientationLoading])

    useEffect(() => {
        document.title = add ? "Add User" : "Edit User"
    }, [add])

    // ---- all hooks above this line ----

    const returnToUsers = () => navigate("/admin/users")
    const showUserSaved = () => {
         dispatch("showToast", {
                    show: true,
                    variant: "success",
                    text: "User Saved",
                    timeout: 5000,
                })
    }
    const title2 = add ? "Add User Account" : "Edit User"

    const handleExport = async () => {
        if (!orientationId) return
        setExporting(true)
        try {
            const response = await wrappedGet(`/survey-metrics/orientation/${orientationId}/export`, {
                responseType: "blob",
            })

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", `orientation-report-${orientationId}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error(error)
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: "An error occurred while downloading the report. Please try again.",
            })
        } finally {
            setExporting(false)
        }
    }

    if (getVisibleTabs(role).length === 0) {
        return (
            <SubHeader title1="OARS" title2="Users" icon="fa-users-cog">
                <div className="text-muted">You do not have access to user accounts.</div>
            </SubHeader>
        )
    }

    const renderBody = () => {
        if (isLoading) return <Skeleton className="w-100 h-400px" />

        if (!user) {
            return (
                <div className="card">
                    <div className="card-body text-center py-15">
                        <div className="fs-4 fw-semibold text-gray-700 mb-5">That user account no longer exists.</div>
                        <button type="button" className="btn btn-primary" onClick={returnToUsers}>
                            Return to Users
                        </button>
                    </div>
                </div>
            )
        }

        const editUser = <EditUser user={user} add={add} role={role} onDone={showUserSaved} />

        if (!isOrientee) return editUser

        return (
            <>
                <RoleTabs tabs={ORIENTEE_TABS} active={tab} onChange={changeTab} />
                {tab === "Edit User & Role" && editUser}
                {tab === "Edit Orientation Details" &&
                    (orientationId ? (
                        <OrienteeDetails
                            orientee={orientation}
                            setOrientee={setOrientation}
                            isOrienteeLoading={isOrientationLoading}
                            editable
                            headerAction={
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleExport}
                                    disabled={exporting}
                                >
                                    <i className="fas fa-file-export fs-3"></i>
                                    {exporting ? "Exporting..." : "Export Orientation Report"}
                                </button>
                            }
                        />
                    ) : (
                        <Skeleton className="w-100 h-400px" />
                    ))}
                {tab === "Orientation Progress Details" && (
                    <div className="card">
                        <div className="card-body text-center py-15 text-muted">
                            Orientation progress data isn't available yet.
                        </div>
                    </div>
                )}
            </>
        )
    }

    if (readOnly) {
        return (
            <div className={cx("d-flex flex-center", {"message-fullpage": true})}>
                <div className="message-box mt-5">
                    <Alert className="d-flex align-items-center" variant="danger">
                        <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                        <p>
                            <strong>Unauthorized Access</strong>
                            <br />
                            You do not have access to user accounts.
                        </p>
                    </Alert>
                </div>
            </div>
        )
    }

    return (
        <>
            <SubHeader title1="OARS" title2={title2} icon="fa-users-cog">
                <div className="d-flex flex-grow-1 justify-content-end align-items-center">
                    <button type="button" className="btn btn-light-primary fw-semibold" onClick={returnToUsers}>
                        <i className="fas fa-arrow-left"></i>Return to Users
                    </button>
                </div>
            </SubHeader>
            <div className="container-fluid mt-8">{renderBody()}</div>
        </>
    )
}

export default EditUserPage
